/**
 * Egress management — wire types, trust-boundary guards, and the read↔edit
 * round-trip (ADR-036 §D-5 / §D-7).
 *
 * Pure, React-free.
 *
 * # Two shapes from one endpoint
 *
 * ``GET /api/admin/peers/interfaces`` is readable by **every authenticated
 * role** (REQ-PEER-042) but is **field-limited** by role: Consumer endpoints,
 * cert-material references and filter *literals* are `admin` /
 * `support_engineer` only, because a producer allow-set or a bounding box
 * describes collection posture (``security.md``; ADR-036 §D-7). The BFF projects
 * accordingly and is the enforcement point.
 *
 * **This module therefore renders what arrived, and never re-derives the
 * field-set from the session's roles.** Two reasons. Re-deriving would put a
 * second, independent copy of a security rule in the client where it can drift
 * from the server's; and it would be a lie either way — a privileged field the
 * BFF withheld cannot be shown however the client feels about the session, and
 * one it sent is already disclosed. Privileged fields are consequently *optional*
 * on :type:`PeerInterfaceView`, and :func:`isPrivilegedView` reports what the
 * response actually carried.
 *
 * # Why hand-rolled guards, not zod
 *
 * The routes return :class:`JSONResponse`, so their response schema is ``{}`` in
 * the committed ``openapi.yaml`` and ``openapi-typescript`` emits no type for
 * it. ``zod`` is not a UI dependency (the tech-stack add is deferred per
 * CLAUDE.md §4.3), so this mirrors the established hand-rolled guard pattern of
 * :file:`@/system-status/status-model` and :file:`@/map/sse-types`: a drifted
 * payload is rejected loudly rather than rendered as a half-populated egress
 * path (CLAUDE.md §8.1).
 *
 * **Request** bodies are the opposite case — ``PeerInterfaceConfig`` *is* on
 * the OpenAPI surface, so the write path uses the generated type verbatim
 * (no hand-written drift).
 *
 * Cross-references:
 *   - ADR-036 §D-5 (control-plane config), §D-7 (RBAC + read field-set).
 *   - REQ-PEER-040/-041/-042/-043/-044/-050.
 *   - `packages/meridian_bff/src/meridian_bff/_peer_routes.py` (the projections).
 */
import type { components } from "@/lib/api-types.gen";
import { defaultTerminalFor } from "@/peers/peer-catalog";
import type { InterfaceState } from "@/peers/peer-catalog";

// ---- Request types (generated — the write path has no hand-written shape) ----

/** The PUT body: a complete Peer Interface (ADR-036 §D-5). */
export type PeerInterfaceConfig = components["schemas"]["PeerInterfaceConfig"];

/** How an interface secures its wire (REQ-PEER-091): plaintext | tls | mtls. */
export type TransportSecurity = PeerInterfaceConfig["transport_security"];
/** Which renderer an interface trusts (REQ-PEER-099): legacy | engine. */
export type ConversionMode = PeerInterfaceConfig["conversion_mode"];
/** One Consumer connection within a PUT body. */
export type PeerInterfaceConnectionConfig = components["schemas"]["PeerInterfaceConnectionConfig"];
/** The versioned filter document (ADR-036 §D-4). */
export type PeerFilterConfig = components["schemas"]["PeerFilterConfig"];
/** The peer PUT body: one external system (ADR-039 §D-1). */
export type PeerConfig = components["schemas"]["PeerConfig"];
/** One node of the filter's clause tree, discriminated on ``kind``. */
export type ClauseNode = NonNullable<PeerFilterConfig["root"]>;
/** A composite (``all_of`` / ``any_of``) clause node. */
export type CompositeNode = components["schemas"]["AllOfNode"] | components["schemas"]["AnyOfNode"];

/** The filter document's schema version this build authors (``FILTER_SCHEMA_VERSION``). */
export const FILTER_SCHEMA_VERSION = 1;
/** The interface-config schema version this build authors. */
export const INTERFACE_SCHEMA_VERSION = 1;

/**
 * Default bound on one framed record — 12 MiB, mirroring migration 045's server default
 * and the connector framework's `DEFAULT_MAX_FRAME_BYTES` (REQ-PEER-054).
 *
 * Restated here because the UI has no import path to either; it is the value a
 * newly-created interface starts at and the fallback when a read view predates the field.
 */
export const DEFAULT_MAX_FRAME_BYTES = 12 * 1024 * 1024;

// ---- Response types (hand-authored — the routes return JSONResponse) --------

/**
 * One Consumer connection as the list/get endpoints project it.
 *
 * Everything from ``endpoint_host`` down is **privileged-only** — the BFF omits
 * the whole block for operator / analyst sessions, because a Consumer's host and
 * port say *where* mission data goes.
 */
export interface PeerInterfaceConnectionView {
  connection_id: string;
  name: string;
  state: InterfaceState;
  role: "pull" | "dial_out";
  endpoint_host?: string | null;
  endpoint_port?: number;
  timeout_seconds?: number;
  retry_max_attempts?: number;
  retry_backoff_seconds?: number;
  filter?: PeerFilterConfig | null;
}

/**
 * The non-disclosing description of a filter every role sees (``FilterSummary``).
 *
 * ``configured: false`` means the interface emits **nothing** (fail-safe
 * default-deny, REQ-PEER-033) — *not* that it is unfiltered. The distinction
 * is the whole point of the field and is surfaced verbatim in the UI copy.
 */
export interface PeerFilterSummary {
  configured: boolean;
  clause_count: number;
  selector_kinds: string[];
}

/** One Peer Interface as the list/get endpoints project it (both field-sets). */
export interface PeerInterfaceView {
  interface_id: string;
  name: string;
  type_id: string;
  enabled: boolean;
  state: InterfaceState;
  connections: PeerInterfaceConnectionView[];
  filter_summary: PeerFilterSummary;
  /**
   * How this interface secures its wire (REQ-PEER-091).
   *
   * **All-roles, and deliberately so** — unlike the framing, terminal and cert references
   * below. Those describe the Consumer's protocol contract or this deployment's delivery
   * mechanics; this describes *whether mission data leaves in the clear*, which is the fact a
   * non-privileged reader most needs. The justification for letting every role read this
   * surface at all is that an operator who cannot see these flows cannot notice one running
   * that should not be (Flow W §2, Flow L §11) — and an unencrypted egress path nobody can
   * see is the sharpest instance of that.
   *
   * It leaks nothing the privileged block guards: a posture is not an endpoint, a credential
   * path or a filter literal.
   */
  transport_security: TransportSecurity;
  /**
   * Which renderer produced what left the deployment (REQ-PEER-099, REQ-UI-153).
   *
   * **All-roles, alongside `transport_security` and for the same reason.** `legacy` and
   * `engine` are not interchangeable to a reader: under `legacy` the proven hand-written
   * renderer is authoritative, under `engine` an installed conversion spec is. An operator who
   * cannot tell them apart cannot notice a wire running on code that was never promoted through
   * §2.6's gate. (A third value, `shadow`, was retired on 2026-08-11.)
   */
  conversion_mode: ConversionMode;
  // ---- privileged-only (absent for operator / analyst) ----
  /**
   * The installed (peer schema, conversion spec) pair this interface renders through.
   *
   * **Privileged**, unlike the mode above. The mode is a posture; this names a specific
   * artifact pair by digest — which mapping this site runs — and that is the same
   * collection-posture signal the endpoints and filter literals are (ADR-036 §D-7).
   */
  conversion_profile_ref?: string | null;
  /**
   * The owning peer (ADR-039 §D-1) — **required to build a write body**.
   *
   * ``PutInterfaceRequest`` is ``{peer_id, interface}``, so an edit cannot be saved
   * without it. It is optional here rather than required because it arrives only in the
   * privileged block: a field-limited reader legitimately has no parentage, and rejecting
   * the whole list over a field only the write path needs would blank every interface from
   * the roster to protect a capability that reader does not have. The write path guards
   * instead — see :func:`useSavePeerInterface`.
   */
  peer_id?: string;
  delivery_profile?: string | null;
  // Wire framing (REQ-PEER-054). Privileged alongside delivery_profile: both describe
  // the Consumer's protocol contract, which the all-roles view deliberately omits.
  framing?: string | null;
  framing_delimiter_hex?: string | null;
  framing_terminator?: string | null;
  max_frame_bytes?: number;
  // The post-codec terminal and its render bound (REQ-PEER-079 / -085). Privileged for
  // the same reason framing is, one step further out: they describe this deployment's
  // delivery mechanics, not whether data is flowing.
  terminal?: string | null;
  max_render_concurrency?: number | null;
  peer_ca_bundle_ref?: string | null;
  meridian_cert_ref?: string | null;
  meridian_key_ref?: string | null;
  filter?: PeerFilterConfig | null;
  metadata?: Record<string, string>;
  schema_version?: number;
  created_by?: string;
}

/**
 * Whether the response carried the privileged field-set.
 *
 * Keyed on ``metadata``: it is the one privileged field the projection **always**
 * writes (``dict(record.metadata)`` — possibly empty, never omitted), so unlike
 * ``filter`` or ``peer_ca_bundle_ref`` it cannot be absent-because-null. A
 * privilege test that a null value could flip would silently downgrade a fully
 * privileged view of an interface that simply has no filter yet.
 */
export function isPrivilegedView(view: PeerInterfaceView): boolean {
  return view.metadata !== undefined;
}

// ---- Trust-boundary guards -------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Parse the ``GET /api/admin/peers/interfaces`` body.
 *
 * Rejects — rather than repairs — a payload that does not carry the fields every
 * role is promised. A half-parsed egress list is the failure mode this feature
 * exists to prevent: an interface silently dropped from the list is an egress
 * path an operator cannot see running, and one with a guessed ``state`` is a
 * kill switch showing the wrong position.
 *
 * @throws Error when the body is not ``{items: [...]}`` of well-formed interfaces.
 */
export function parsePeerInterfaceList(body: unknown): PeerInterfaceView[] {
  if (!isRecord(body) || !Array.isArray(body.items)) {
    throw new Error("peer interface list: expected an object with an `items` array");
  }
  return body.items.map(parsePeerInterface);
}

/**
 * Parse one interface object from a list or ``GET …/interfaces/{id}`` body.
 *
 * @throws Error when a field every role is promised is missing or mistyped.
 */
export function parsePeerInterface(body: unknown): PeerInterfaceView {
  if (!isRecord(body)) throw new Error("peer interface: expected an object");
  const {
    interface_id,
    name,
    type_id,
    enabled,
    state,
    connections,
    filter_summary,
    transport_security,
    conversion_mode,
    metadata,
    schema_version,
    created_by,
  } = body;
  if (typeof interface_id !== "string" || typeof name !== "string" || typeof type_id !== "string") {
    throw new Error("peer interface: interface_id, name and type_id must be strings");
  }
  if (typeof enabled !== "boolean") {
    throw new Error(`peer interface ${name}: enabled must be a boolean`);
  }
  if (!Array.isArray(connections)) {
    throw new Error(`peer interface ${name}: connections must be an array`);
  }
  const view: PeerInterfaceView = {
    interface_id,
    name,
    type_id,
    enabled,
    state: parseInterfaceState(state, name),
    connections: connections.map((c) => parsePeerConnection(c, name)),
    filter_summary: parseFilterSummary(filter_summary),
    // Parsed strictly rather than defaulted. A missing or unrecognised mode means the client
    // and the BFF disagree about the shape of a security-relevant field, and silently
    // substituting one would let the editor round-trip a value the server never sent —
    // rewriting a live interface's wire security on an unrelated save.
    transport_security: parseTransportSecurity(transport_security, name),
    conversion_mode: parseConversionMode(conversion_mode, name),
  };
  // Privileged block — present as a unit or not at all. `metadata` decides
  // (see `isPrivilegedView`); the rest are copied through as-is because their
  // shapes are the generated request types the BFF echoes back.
  if (isRecord(metadata)) {
    view.metadata = metadata as Record<string, string>;
    if (typeof body.peer_id === "string") view.peer_id = body.peer_id;
    view.delivery_profile = asNullableString(body.delivery_profile);
    view.framing = asNullableString(body.framing);
    view.framing_delimiter_hex = asNullableString(body.framing_delimiter_hex);
    view.framing_terminator = asNullableString(body.framing_terminator);
    if (typeof body.max_frame_bytes === "number") view.max_frame_bytes = body.max_frame_bytes;
    // The terminal and its render bound (REQ-PEER-079 / -085) ride the privileged block beside
    // framing and must be copied for the same reason: `toInterfaceConfig` substitutes the TYPE
    // default for an absent terminal and "inherit" for an absent bound, so a copy missing here
    // is a `spooled` interface silently written back `direct` — and a configured bound written
    // back `null` — on any unrelated edit. That is what happened until 2026-10-15: the write
    // side carried the fallback, the read side never carried the value.
    view.terminal = asNullableString(body.terminal);
    view.max_render_concurrency = asNullableNumber(body.max_render_concurrency);
    view.conversion_profile_ref = asNullableString(body.conversion_profile_ref);
    view.peer_ca_bundle_ref = asNullableString(body.peer_ca_bundle_ref);
    view.meridian_cert_ref = asNullableString(body.meridian_cert_ref);
    view.meridian_key_ref = asNullableString(body.meridian_key_ref);
    view.filter = isRecord(body.filter) ? (body.filter as PeerFilterConfig) : null;
    if (typeof schema_version === "number") view.schema_version = schema_version;
    if (typeof created_by === "string") view.created_by = created_by;
  }
  return view;
}

/**
 * Coerce a wire ``state`` token, **failing safe to ``stopped``**.
 *
 * An unrecognized token must never read as ``started``: the pill is how an
 * operator confirms a flow they stopped is actually stopped, and guessing
 * "running" on drift would be a false negative on the one control this feature
 * provides (CLAUDE.md §8.1).
 *
 * @throws Error when the field is absent entirely — that is drift, not a value.
 */
function parseInterfaceState(value: unknown, context: string): InterfaceState {
  if (typeof value !== "string") {
    throw new Error(`egress ${context}: state must be a string`);
  }
  return value === "started" ? "started" : "stopped";
}

function parsePeerConnection(body: unknown, interfaceName: string): PeerInterfaceConnectionView {
  if (!isRecord(body)) throw new Error(`peer interface ${interfaceName}: malformed connection`);
  const { connection_id, name, role } = body;
  if (typeof connection_id !== "string" || typeof name !== "string") {
    throw new Error(`peer interface ${interfaceName}: connection_id and name must be strings`);
  }
  const view: PeerInterfaceConnectionView = {
    connection_id,
    name,
    state: parseInterfaceState(body.state, `connection ${name}`),
    role: role === "dial_out" ? "dial_out" : "pull",
  };
  if (typeof body.endpoint_port === "number") {
    view.endpoint_host = asNullableString(body.endpoint_host);
    view.endpoint_port = body.endpoint_port;
    view.timeout_seconds = typeof body.timeout_seconds === "number" ? body.timeout_seconds : 30;
    view.retry_max_attempts =
      typeof body.retry_max_attempts === "number" ? body.retry_max_attempts : 3;
    view.retry_backoff_seconds =
      typeof body.retry_backoff_seconds === "number" ? body.retry_backoff_seconds : 1;
    view.filter = isRecord(body.filter) ? (body.filter as PeerFilterConfig) : null;
  }
  return view;
}

/**
 * Parse the filter summary, failing safe to *unconfigured* when it is absent or
 * malformed. Unconfigured means "emits nothing", so the safe reading is also the
 * conservative one — the opposite guess would tell an operator a flow is
 * constrained when the UI has no idea.
 */
/**
 * Parse the wire-security mode, refusing anything the client does not recognise.
 *
 * Strict on purpose. A default here would be indistinguishable from the server actually
 * reporting that value, and the field decides whether mission data leaves in the clear — so an
 * unrecognised mode is a client/server disagreement to surface, not one to paper over. The same
 * reasoning the read view carries for refusing to build a write body from a field-limited read.
 */
function parseTransportSecurity(value: unknown, name: string): TransportSecurity {
  if (value === "plaintext" || value === "tls" || value === "mtls") {
    return value;
  }
  throw new Error(
    `peer interface ${name}: unrecognised transport_security ${JSON.stringify(value)}`,
  );
}

/**
 * Parse the conversion mode, refusing anything the client does not recognise.
 *
 * Strict for the same reason {@link parseTransportSecurity} is, and with a second one: this
 * value round-trips into the PUT body, so a defaulted `legacy` would be written back and would
 * demote a promoted interface on an unrelated save. A default here is indistinguishable from
 * the server reporting that value.
 */
function parseConversionMode(value: unknown, name: string): ConversionMode {
  if (value === "legacy" || value === "engine") {
    return value;
  }
  throw new Error(`peer interface ${name}: unrecognised conversion_mode ${JSON.stringify(value)}`);
}

function parseFilterSummary(value: unknown): PeerFilterSummary {
  if (!isRecord(value)) return { configured: false, clause_count: 0, selector_kinds: [] };
  return {
    configured: value.configured === true,
    clause_count: typeof value.clause_count === "number" ? value.clause_count : 0,
    selector_kinds: Array.isArray(value.selector_kinds)
      ? value.selector_kinds.filter((k): k is string => typeof k === "string")
      : [],
  };
}

function asNullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

// ---- Read → edit round-trip ------------------------------------------------

/**
 * Rebuild the PUT body from a privileged read projection.
 *
 * The mutation surface is a whole-document PUT, so editing one field means
 * re-sending every other one; this is the lossless inverse of the BFF's
 * ``_interface_to_dict(full=True)``. The one reshaping is the retry policy,
 * which the read flattens (``retry_max_attempts`` / ``retry_backoff_seconds``)
 * and the config nests (``retry: {max_attempts, backoff_seconds}``).
 *
 * @throws Error when handed a non-privileged view — a PUT built from the
 *   field-limited projection would blank the endpoints, cert references and
 *   filter of a live egress path. Refusing is the only safe reading; the UI
 *   never offers the editor for such a session anyway (defence in depth).
 */
export function toInterfaceConfig(view: PeerInterfaceView): PeerInterfaceConfig {
  if (!isPrivilegedView(view)) {
    throw new Error(
      "toInterfaceConfig: refusing to build a write body from a field-limited read view",
    );
  }
  return {
    interface_id: view.interface_id,
    name: view.name,
    type_id: view.type_id,
    enabled: view.enabled,
    state: view.state,
    // Carried through from the read, never defaulted. Defaulting here would silently rewrite a
    // live interface's WIRE SECURITY on any unrelated edit — the same shape as the `terminal`
    // bug this file already carries a guard for, but on the field that decides whether mission
    // data leaves in the clear. It is in the all-roles block precisely so this round-trip works.
    transport_security: view.transport_security,
    // Carried through for the same reason, with a sharper failure than framing's. Defaulting
    // `conversion_mode` to `legacy` on an unrelated edit would silently return a PROMOTED
    // interface to the hand-written renderer — the engine stops being authoritative and nothing
    // says so, which is precisely the state §2.6's promotion gate exists to make deliberate.
    // And dropping the ref while keeping a non-`legacy` mode is a guaranteed 422 (the config
    // model's cross-field rule), so the operator would find an unrelated field uneditable with
    // no indication why — the `terminal` bug's exact shape. The ref is privileged and this
    // function already refuses a non-privileged view, so both are always present here.
    conversion_profile_ref: view.conversion_profile_ref ?? null,
    conversion_mode: view.conversion_mode,
    delivery_profile: (view.delivery_profile ?? null) as PeerInterfaceConfig["delivery_profile"],
    // Framing must round-trip (REQ-PEER-054). This builds the PUT body from a read
    // view, so dropping these would silently reset an interface's wire framing to the
    // default on any *unrelated* edit — the Consumer would stop being able to parse the
    // stream and nothing in the UI would have said so.
    framing: (view.framing ?? "length_prefix") as PeerInterfaceConfig["framing"],
    framing_delimiter_hex: view.framing_delimiter_hex ?? "1e",
    framing_terminator: view.framing_terminator ?? null,
    max_frame_bytes: view.max_frame_bytes ?? DEFAULT_MAX_FRAME_BYTES,
    // The terminal must round-trip for the same reason framing does, with a sharper
    // failure: dropping it on an unrelated edit would rewrite a spooled interface to
    // `direct`, which for a CDS type the BFF then 422s (REQ-PEER-081) — so the operator
    // would find an unrelated field uneditable with no indication why. For a type that
    // does NOT require a spool the BFF accepts the rewrite, so the loss is silent. This
    // fallback is only half the guard: `parsePeerInterface` copying the field from the
    // read is the other half, and until 2026-10-15 it did not (both fields fell back on
    // every save).
    terminal: (view.terminal ??
      defaultTerminalFor(view.type_id)) as PeerInterfaceConfig["terminal"],
    max_render_concurrency: view.max_render_concurrency ?? null,
    peer_ca_bundle_ref: view.peer_ca_bundle_ref ?? null,
    meridian_cert_ref: view.meridian_cert_ref ?? null,
    meridian_key_ref: view.meridian_key_ref ?? null,
    filter: view.filter ?? null,
    connections: view.connections.map(toConnectionConfig),
    metadata: view.metadata ?? {},
    schema_version: view.schema_version ?? INTERFACE_SCHEMA_VERSION,
  };
}

/** Rebuild one connection's PUT shape from its read projection (see {@link toInterfaceConfig}). */
export function toConnectionConfig(
  view: PeerInterfaceConnectionView,
): PeerInterfaceConnectionConfig {
  return {
    connection_id: view.connection_id,
    name: view.name,
    role: view.role,
    // `pull` must carry no host at all — the config validator rejects one as a
    // silently-ignored endpoint, which is how a connection ends up pointing
    // somewhere nobody intended.
    endpoint_host: view.role === "dial_out" ? (view.endpoint_host ?? null) : null,
    endpoint_port: view.endpoint_port ?? 0,
    state: view.state,
    timeout_seconds: view.timeout_seconds ?? 30,
    retry: {
      max_attempts: view.retry_max_attempts ?? 3,
      backoff_seconds: view.retry_backoff_seconds ?? 1,
    },
    filter: view.filter ?? null,
  };
}

/**
 * A brand-new interface, **stopped** (REQ-PEER-041) and with **no filter**
 * (so it denies everything until one is authored — REQ-PEER-033).
 *
 * Both defaults are the fail-safe ones and neither is offered as a choice at
 * create time: the UI deliberately has no "create started" affordance. The
 * pre-UI start path is the seed file's ``state: started`` (ADR-036 §D-7), and
 * the runtime start is an explicit, separately-audited action — a create that
 * could begin emitting in the same click would collapse those two decisions
 * into one.
 */
export function newInterfaceConfig(interfaceId: string, typeId: string): PeerInterfaceConfig {
  return {
    interface_id: interfaceId,
    name: "",
    type_id: typeId,
    enabled: true,
    state: "stopped",
    // The fail-safe default (REQ-PEER-091). A new interface is born mutually authenticated and
    // an operator must deliberately choose anything weaker; `plaintext` is never arrived at by
    // leaving a form alone. Types whose wire fixes the mode (the CDS link) are refused a create
    // here anyway — the catalog gates the type list.
    transport_security: "mtls",
    // No conversion, legacy renderer (REQ-PEER-099). The fail-safe pair, and the only one a
    // create form could offer: a profile has to be installed before it can be referenced, so a
    // new interface cannot name one, and a non-`legacy` mode without a ref is a 422.
    conversion_profile_ref: null,
    conversion_mode: "legacy",
    delivery_profile: null,
    // The migration-045 defaults: length-prefix at the framework's default frame bound,
    // i.e. what every direct interface did before framing was configurable.
    framing: "length_prefix",
    framing_delimiter_hex: "1e",
    framing_terminator: null,
    max_frame_bytes: DEFAULT_MAX_FRAME_BYTES,
    // From the type, not a blanket `direct`: a CDS interface created `direct` is one the
    // BFF refuses with a 422 (REQ-PEER-081), so the create form would offer a shape the
    // server will not accept.
    terminal: defaultTerminalFor(typeId),
    max_render_concurrency: null,
    peer_ca_bundle_ref: null,
    meridian_cert_ref: null,
    meridian_key_ref: null,
    filter: null,
    connections: [],
    metadata: {},
    schema_version: INTERFACE_SCHEMA_VERSION,
  };
}

/** A brand-new Consumer connection: **stopped**, pull-direction (ADR-017 §8 default). */
export function newConnectionConfig(connectionId: string): PeerInterfaceConnectionConfig {
  return {
    connection_id: connectionId,
    name: "",
    role: "pull",
    endpoint_host: null,
    endpoint_port: 9443,
    state: "stopped",
    timeout_seconds: 30,
    retry: { max_attempts: 3, backoff_seconds: 1 },
    filter: null,
  };
}

// ---- Peers (ADR-039 §D-1) --------------------------------------------------

/**
 * One Peer as the roster endpoint projects it.
 *
 * The same two-shapes-from-one-endpoint rule as {@link PeerInterfaceView}: every
 * authenticated role sees that the peer exists and whether it is running — a
 * silenced flow an operator cannot see is worse than the silencing — while
 * ``metadata`` (free text that has carried ticket references and points of
 * contact) and ``created_by`` are privileged.
 *
 * ``interfaces`` is deliberately absent rather than empty. The roster and a
 * peer's interface list are separate reads (``GET …/{peer_id}/interfaces``), and
 * an always-empty key here would read as "this peer has no interfaces" to
 * anyone who did not know to look elsewhere.
 */
export interface PeerView {
  peer_id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  state: InterfaceState;
  schema_version?: number;
  /**
   * How many interfaces this peer owns, and how many are started (REQ-UI-140).
   *
   * Served by the **list** route only, so both are optional and are ``undefined``
   * — never 0 — on a single-peer read. A zero would say "no interfaces" where
   * the honest answer is "not counted here", and on this surface that reads as
   * nothing leaving through the peer.
   */
  interface_count?: number;
  started_interface_count?: number;
  // ---- privileged-only (absent for operator / analyst) ----
  metadata?: Record<string, string>;
  created_by?: string;
}

/**
 * Whether the peer response carried the privileged field-set.
 *
 * Keyed on ``metadata`` for the same reason {@link isPrivilegedView} is: it is
 * the one privileged field the projection always writes (possibly empty, never
 * omitted), so unlike ``created_by`` it cannot be absent-because-null.
 */
export function isPrivilegedPeerView(view: PeerView): boolean {
  return view.metadata !== undefined;
}

/**
 * Parse the ``GET /api/admin/peers`` body.
 *
 * @throws Error when the body is not ``{items: [...]}`` of well-formed peers.
 */
export function parsePeerList(body: unknown): PeerView[] {
  if (!isRecord(body) || !Array.isArray(body.items)) {
    throw new Error("peer list: expected an object with an `items` array");
  }
  return body.items.map(parsePeer);
}

/**
 * Parse one peer object.
 *
 * Rejects rather than repairs, for the reason {@link parsePeerInterface} does:
 * a peer silently dropped from the roster is a system an operator cannot see
 * data flowing to, and one with a guessed ``state`` is a kill switch showing
 * the wrong position.
 *
 * @throws Error when a field every role is promised is missing or mistyped.
 */
export function parsePeer(body: unknown): PeerView {
  if (!isRecord(body)) throw new Error("peer: expected an object");
  const { peer_id, name, enabled, metadata, schema_version, created_by } = body;
  if (typeof peer_id !== "string" || typeof name !== "string") {
    throw new Error("peer: peer_id and name must be strings");
  }
  if (typeof enabled !== "boolean") {
    throw new Error(`peer ${name}: enabled must be a boolean`);
  }
  const view: PeerView = {
    peer_id,
    name,
    description: asNullableString(body.description),
    enabled,
    state: parseInterfaceState(body.state, `peer ${name}`),
  };
  if (typeof schema_version === "number") view.schema_version = schema_version;
  if (typeof body.interface_count === "number") view.interface_count = body.interface_count;
  if (typeof body.started_interface_count === "number") {
    view.started_interface_count = body.started_interface_count;
  }
  if (isRecord(metadata)) {
    view.metadata = metadata as Record<string, string>;
    if (typeof created_by === "string") view.created_by = created_by;
  }
  return view;
}

/**
 * Rebuild the peer PUT body from a read projection.
 *
 * ``interfaces`` is sent empty and that is not a loss: the peer PUT deliberately
 * does not write its interface set (``PutPeerRequest`` documents the same
 * separation server-side), so a rename cannot silently delete everything beneath
 * it. Interfaces are edited through their own routes.
 *
 * @throws Error when handed a non-privileged view — a PUT built from the
 *   field-limited projection would blank the peer's operator annotations.
 */
export function toPeerConfig(view: PeerView): PeerConfig {
  if (!isPrivilegedPeerView(view)) {
    throw new Error("toPeerConfig: refusing to build a write body from a field-limited read view");
  }
  return {
    peer_id: view.peer_id,
    name: view.name,
    description: view.description,
    enabled: view.enabled,
    state: view.state,
    interfaces: [],
    metadata: view.metadata ?? {},
    schema_version: view.schema_version ?? INTERFACE_SCHEMA_VERSION,
  };
}

/**
 * A brand-new peer, **stopped** (REQ-PEER-061).
 *
 * Stopped is the fail-safe default and is not offered as a choice at create
 * time, matching {@link newInterfaceConfig}: creating a peer and starting data
 * flowing to it are two decisions, and a create that could begin emitting in the
 * same click would collapse them into one.
 */
export function newPeerConfig(peerId: string): PeerConfig {
  return {
    peer_id: peerId,
    name: "",
    description: null,
    enabled: true,
    state: "stopped",
    interfaces: [],
    metadata: {},
    schema_version: INTERFACE_SCHEMA_VERSION,
  };
}

/**
 * The whole-document PUT body with ``enabled`` flipped (REQ-UI-154).
 *
 * Lives beside {@link toInterfaceConfig} rather than in the pane that calls it:
 * enable/disable has no dedicated endpoint, so it is a full re-send, and the
 * round-trip rules that make that safe — carry every field, never default one —
 * are this module's.
 */
export function withEnabled(view: PeerInterfaceView, enabled: boolean): PeerInterfaceConfig {
  return { ...toInterfaceConfig(view), enabled };
}
